// Keep themes — the look a host picks when starting a Keep. Eight designs spanning the whole
// calendar (weddings → birthdays → showers → holidays → trips). Each preset sets a full palette,
// a line motif, and a display type. Used by the dashboard "Start a Keep" picker (card previews +
// seeding branding on create) and by the guest app (applies the palette + renders the motif).
//
// A preset is a *starting point*: on create its values are copied into the Keep's branding.theme,
// which the host can then fine-tune. branding.theme.preset records which theme it started from so
// the guest app knows the structural bits (motif, display styling) that aren't plain colors.

export type ThemeMotif =
  | "floral" // legacy watercolor bougainvillea (keeps created before the theme system)
  | "rule"
  | "deco"
  | "arch"
  | "confetti"
  | "dots"
  | "sprigstar"
  | "horizon";

/** Display-type family for the hero name. Drives the guest hero's script class + font. */
export type ThemeDisplay = "serif" | "sans" | "serifcaps" | "round" | "script";

/** Hero name style class applied in the guest app (paired with the theme's --font-script). */
export type ThemeScriptClass = "" | "sc-sans" | "sc-caps" | "sc-round";

export interface ThemePreset {
  id: string;
  name: string;
  tag: string;
  /** Who it's for, shown as the card eyebrow. */
  occasion: string;
  desc: string;
  /** True for the four gathering-focused additions (shows a "New" badge in the picker). */
  isNew?: boolean;
  /** Dark-canvas theme (light text on a deep background). */
  dark?: boolean;
  motif: ThemeMotif;
  display: ThemeDisplay;
  scriptClass: ThemeScriptClass;
  // ── full guest palette (CSS custom properties) ──
  ink: string;
  inkSoft: string;
  muted: string;
  line: string;
  accent: string;
  accentDeep: string;
  accentSoft: string;
  page: string;
  paper: string;
  card: string;
  scriptFont: string;
  // ── picker preview data ──
  swatches: [string, string, string, string];
  tiles: [[string, string], [string, string], [string, string], [string, string]];
  sampleNames: string;
  sampleDate: string;
}

const SERIF = '"Newsreader", Georgia, serif';
const SANS = '"Archivo", system-ui, sans-serif';
const ROUND = '"Yeseva One", Georgia, serif';

export const THEME_PRESETS: ThemePreset[] = [
  {
    id: "garden", name: "Keepsake", tag: "Warm · timeless", occasion: "Weddings & anniversaries",
    desc: "Warm terracotta and olive on soft cream, elegant Newsreader italics, clean hairline rules. The signature MomentKeep look.",
    motif: "rule", display: "serif", scriptClass: "",
    ink: "#2c2823", inkSoft: "#7b7264", muted: "#6f6658", line: "rgba(44,40,35,0.14)",
    accent: "#c26b3c", accentDeep: "#a1512a", accentSoft: "#f4e1d0",
    page: "#efe7d9", paper: "#faf6ef", card: "#fffdf9", scriptFont: SERIF,
    swatches: ["#c26b3c", "#2c2823", "#6f7a4b", "#faf6ef"],
    tiles: [["#f2d9c4", "#e3b189"], ["#e7e3cf", "#cdd0ab"], ["#f1e2d0", "#e3c19b"], ["#f4ddc2", "#e6a982"]],
    sampleNames: "Taner & Abbie", sampleDate: "June 20 · 2026",
  },
  {
    id: "minimal", name: "Modern Minimal", tag: "Clean · editorial", occasion: "Engagements & modern",
    desc: "Stark white, oversized grotesque type, hairline rules. The photos do all the talking.",
    motif: "rule", display: "sans", scriptClass: "sc-sans",
    ink: "#161616", inkSoft: "#8c8c8c", muted: "#7c7c7c", line: "rgba(20,20,20,0.11)",
    accent: "#e2603f", accentDeep: "#c2492b", accentSoft: "#f8e2da",
    page: "#f1f0ed", paper: "#ffffff", card: "#ffffff", scriptFont: SANS,
    swatches: ["#141414", "#e2603f", "#9a9a9a", "#ffffff"],
    tiles: [["#ededed", "#d9d9d9"], ["#e4e4e4", "#cfcfcf"], ["#f0f0f0", "#dcdcdc"], ["#e8e8e8", "#d2d2d2"]],
    sampleNames: "MAE & JO", sampleDate: "09.14.2026",
  },
  {
    id: "deco", name: "Gilded Deco", tag: "Glamorous · vintage", occasion: "Milestone birthdays · black-tie",
    desc: "Emerald and gold, geometric sunbursts, tall serif capitals. Black-tie evening glamour.",
    motif: "deco", display: "serifcaps", scriptClass: "sc-caps", dark: true,
    ink: "#f1e6cf", inkSoft: "#9bb0a2", muted: "#9bb0a2", line: "rgba(241,230,207,0.18)",
    accent: "#cba53a", accentDeep: "#e6c869", accentSoft: "#26443a",
    page: "#0f1d18", paper: "#14271f", card: "#1c3a2f", scriptFont: SERIF,
    swatches: ["#13251e", "#c9a227", "#1d3a30", "#f1e6cf"],
    tiles: [["#1f3a30", "#16291f"], ["#2c4a3b", "#1c3329"], ["#3a4a2f", "#23301f"], ["#1e352b", "#142019"]],
    sampleNames: "Vivienne & Charles", sampleDate: "MCMXXVI",
  },
  {
    id: "boho", name: "Boho Sunset", tag: "Warm · retro", occasion: "Showers & festival weddings",
    desc: "Terracotta, ochre and olive with a rising-sun arch and rounded display type. Golden-hour desert.",
    motif: "arch", display: "round", scriptClass: "sc-round",
    ink: "#5a3825", inkSoft: "#a07a55", muted: "#9a7657", line: "rgba(90,56,37,0.18)",
    accent: "#c5683f", accentDeep: "#9c4a2c", accentSoft: "#f1ddc8",
    page: "#f0e2cf", paper: "#fbf2e5", card: "#fffaf1", scriptFont: ROUND,
    swatches: ["#c5683f", "#d99a3c", "#7d7a3a", "#f3e7d6"],
    tiles: [["#e8b988", "#d2823f"], ["#d99a3c", "#b86a2e"], ["#c5683f", "#9c4a2c"], ["#c6b06a", "#9d8a3f"]],
    sampleNames: "Sol & River", sampleDate: "Autumn 2026",
  },
  {
    id: "confetti", name: "Confetti", tag: "Playful · joyful", occasion: "Birthdays & parties", isNew: true,
    desc: "Poppy raspberry on soft blush with a hand-tossed confetti scatter and rounded type. Made for a good time.",
    motif: "confetti", display: "round", scriptClass: "sc-round",
    ink: "#3a2230", inkSoft: "#9a7d88", muted: "#8f7480", line: "rgba(58,34,48,0.13)",
    accent: "#e8506b", accentDeep: "#c93a54", accentSoft: "#fbdbe0",
    page: "#fbf1ec", paper: "#fffaf6", card: "#ffffff", scriptFont: ROUND,
    swatches: ["#e8506b", "#3a2230", "#f0a93c", "#fbf1ec"],
    tiles: [["#fbd0d9", "#f2909f"], ["#fbe4c4", "#f2b46a"], ["#f6c3cd", "#e8506b"], ["#f9dcc6", "#f0a93c"]],
    sampleNames: "Mia turns SIX", sampleDate: "Sat · April 12",
  },
  {
    id: "little", name: "Little One", tag: "Soft · tender", occasion: "Baby showers & arrivals", isNew: true,
    desc: "Powder blue and sage on pale cream with a delicate dotted garland. Gentle enough for the newest guest.",
    motif: "dots", display: "serif", scriptClass: "",
    ink: "#3f4a44", inkSoft: "#8b9a90", muted: "#82918a", line: "rgba(63,74,68,0.12)",
    accent: "#8aa9bd", accentDeep: "#6a8896", accentSoft: "#e2edf1",
    page: "#eef1ec", paper: "#fbfcfa", card: "#ffffff", scriptFont: SERIF,
    swatches: ["#8aa9bd", "#3f4a44", "#a9c0a0", "#eef1ec"],
    tiles: [["#dbe6ec", "#b6cdd9"], ["#e2ebdd", "#c2d4bc"], ["#e6edf0", "#c8dae2"], ["#e9ede4", "#cbd8c6"]],
    sampleNames: "Baby Ellis", sampleDate: "A Little Shower",
  },
  {
    id: "evergreen", name: "Evergreen", tag: "Cozy · festive", occasion: "Holidays & family gatherings", isNew: true, dark: true,
    desc: "Deep pine and warm berry with gilded stars and pine sprigs. An heirloom feel for the whole season.",
    motif: "sprigstar", display: "serifcaps", scriptClass: "sc-caps",
    ink: "#f0e7d6", inkSoft: "#a6b6a4", muted: "#a6b6a4", line: "rgba(240,231,214,0.16)",
    accent: "#d67a5f", accentDeep: "#ec9a80", accentSoft: "#2d3e33",
    page: "#152220", paper: "#1b2a23", card: "#243228", scriptFont: SERIF,
    swatches: ["#152220", "#d67a5f", "#e0c47a", "#f0e7d6"],
    tiles: [["#20342b", "#16261e"], ["#2c4636", "#1d3227"], ["#3a3a26", "#242618"], ["#243228", "#172420"]],
    sampleNames: "The Hollis Family", sampleDate: "Christmas 2026",
  },
  {
    id: "voyage", name: "Voyage", tag: "Breezy · retro", occasion: "Vacations & reunions", isNew: true,
    desc: "Sea-teal on warm sand with a rising-sun horizon and clean sans caps. A postcard from wherever you gathered.",
    motif: "horizon", display: "sans", scriptClass: "sc-sans",
    ink: "#24404a", inkSoft: "#7d8f8f", muted: "#77898a", line: "rgba(36,64,74,0.13)",
    accent: "#2f7d92", accentDeep: "#1f5f72", accentSoft: "#d3e6e6",
    page: "#eae3d5", paper: "#fbf7ee", card: "#fffdf7", scriptFont: SANS,
    swatches: ["#2f7d92", "#24404a", "#dba24a", "#eae3d5"],
    tiles: [["#bcd3d4", "#7fa9ac"], ["#e6d3a8", "#cfa85f"], ["#a9c6c9", "#5f8f92"], ["#ddcdb0", "#c2a878"]],
    sampleNames: "COSTA RICA '26", sampleDate: "The Reyes Reunion",
  },
];

export const DEFAULT_THEME_ID = "garden";

/** Keeps created before the theme system carry no preset; they used the watercolor bougainvillea
 * florals and default script styling. Render that so legacy Keeps are unchanged. */
export const LEGACY_MOTIF: ThemeMotif = "floral";

export function themePreset(id: string | null | undefined): ThemePreset | undefined {
  return THEME_PRESETS.find((p) => p.id === id);
}

/** The branding.theme payload seeded from a preset when a Keep is created. Copied (not referenced)
 * so later fine-tuning a Keep's colors never mutates the shared preset — and so the guest app can
 * apply the palette directly without a lookup. `preset` records the structural identity. */
export function brandingThemeFromPreset(p: ThemePreset): {
  preset: string; motif: ThemeMotif;
  accent: string; accentDeep: string; accentSoft: string;
  page: string; paper: string; card: string; ink: string; inkSoft: string; line: string; muted: string;
  scriptFont: string; dark: boolean;
} {
  return {
    preset: p.id, motif: p.motif,
    accent: p.accent, accentDeep: p.accentDeep, accentSoft: p.accentSoft,
    page: p.page, paper: p.paper, card: p.card, ink: p.ink, inkSoft: p.inkSoft, line: p.line, muted: p.muted,
    scriptFont: p.scriptFont, dark: !!p.dark,
  };
}
